import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { MessageCircle, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small "↔ N" chip shown beside a verse when the user has one or more recent
 * AI-companion conversations that cited this verse. Clicking opens a dialog
 * listing the questions and a "Continue this conversation" button that
 * navigates to the AI page with the selected conversation pre-loaded.
 *
 * Count is supplied by the parent (which fetches a single per-chapter
 * summary via /api/lumera/verse-trail/chapter), so this component is
 * render-cheap even for 176-verse Psalm 119.
 *
 * Props:
 *   verseReference  - e.g. "Romans 8:1"
 *   count           - number of conversations referencing this verse
 *   language        - 'en' | 'es'
 */
const VerseTrailChip = ({ verseReference, count = 0, language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!count) return null;

  const loadTrail = async () => {
    setLoading(true);
    try {
      // The trail dialog fetches full conversation details only when opened,
      // not during chapter render.
      const chapterRef = verseReference.split(':')[0];
      const r = await axios.get(`${BACKEND_URL}/api/lumera/verse-trail`, {
        params: { reference: chapterRef, limit: 6 },
        withCredentials: true,
      });
      setTrail(r.data?.trail || []);
    } catch (err) {
      console.error('verse trail load', err);
      setTrail([]);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
    if (!trail.length) loadTrail();
  };

  const openConversation = (conversationId) => {
    setOpen(false);
    // LumeraCompanion reads ?conversation=… query and auto-loads it.
    navigate(`/ai-companion?conversation=${encodeURIComponent(conversationId)}`);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(language === 'es' ? 'es-ES' : undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        title={
          language === 'es'
            ? `${count} conversaciones con IA citan este versículo`
            : `${count} AI conversations referenced this verse`
        }
        data-testid={`verse-trail-chip-${verseReference.replace(/\s+/g, '_')}`}
      >
        <MessageCircle className="w-2.5 h-2.5" />
        {count}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[80vh] overflow-y-auto"
          data-testid="verse-trail-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Rastro de conversación' : 'Conversation trail'}
              <span className="block text-sm font-normal text-muted-foreground">
                {verseReference}
              </span>
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Preguntas recientes que hiciste y que mencionaron este pasaje.'
                : 'Recent questions you asked that referenced this passage.'}
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-8 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              {language === 'es' ? 'Cargando…' : 'Loading…'}
            </div>
          )}

          {!loading && trail.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">
              {language === 'es' ? 'No hay conversaciones previas.' : 'No prior conversations.'}
            </p>
          )}

          {!loading && trail.length > 0 && (
            <ul className="divide-y divide-border">
              {trail.map((t) => (
                <li key={t.conversation_id} className="py-3 space-y-1">
                  <p className="text-sm font-medium line-clamp-2">
                    {t.question || '(no question recorded)'}
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                      {(t.matched_references || []).map((r) => (
                        <span
                          key={r}
                          className="px-1.5 py-0.5 bg-muted rounded"
                        >
                          {r}
                        </span>
                      ))}
                      {t.updated_at && (
                        <span className="ml-1">• {fmtDate(t.updated_at)}</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openConversation(t.conversation_id)}
                      data-testid={`verse-trail-open-${t.conversation_id}`}
                    >
                      {language === 'es' ? 'Continuar' : 'Continue'}
                      <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerseTrailChip;
